import React, { Component } from 'react';
import ReactDOM from 'react-dom'
import {GoogleApiWrapper} from 'google-maps-react';

import {firebaseBoundsToGoogle, northAmerica, zoomFromBounds} from "../../helper_functions";
import './BoundsOnMap.css';

/**
 * The first of two Google Map components for bounds.  Takes bounds, height and a callback for the Edit link in the caption.
 * Shows bounds and allows clicking through to the truly edibable map, called UpdateBoundsOnMap
 *
 * PROPS
 *
 * width height  The dimensions of this component
 * google        Reference to google maps lib
 * bounds        The bounds box to display on the map.  Can be null for no bounds.
 * isClue        Whether to draw the main bounds as GameBounds (dark green) or as Clue Bounds (green-blue).
 * subBounds     Array of sub bounds to drawn if present.  (In clue color).
 * subBoundsCallback    If non-null, number the subbounds according to their position in the array,
 *               starting at 1.  Make the selectable and invoke this method as a call back.
 * hasDefaultLatLon  whether latDefault and lonDefault are present.
 * latDefault lonDefault  The default coordinates at which to display map if bounds not present
 * boundsForScale  Use these bounds to calculate scale.  If absent, scale is computed from regular bounds, or, if none, a default.
 * caption       Text to display underneath the image
 * editable      Whether this map can be used as a link to an editable map
 * editCallback  Function to call when user clicks on map.
 */
class BoundsOnMap extends Component {
    // Called once after first render
    componentDidMount() {
       this.createMap();
    }
    //
    // // called after subsequent renders
    componentDidUpdate(prevProps, prevState) {
        this.createMap();
    }

   // // do the work creating/attaching map to DOM
   createMap() {
      const {google, bounds, isClue, subBounds, subBoundsCallback, hasDefaultLatLon, latDefault, lonDefault, boundsForScale} = this.props;

      const maps = google.maps; // sets maps to google maps props

      // Get game bounds from props, or default
      let hasBounds = !!bounds;
      let hasBoundsForScale = !!boundsForScale;
      const hasSubBounds = !!subBounds;

      const gBounds = hasBounds ? firebaseBoundsToGoogle(bounds, google) :
         hasBoundsForScale ? firebaseBoundsToGoogle(boundsForScale, google) :
            hasDefaultLatLon ? BoundsOnMap.defaultBoundingBoxForLocation(latDefault, lonDefault, google) :
               northAmerica(google);

      const mapRef = this.refs.map; // looks for HTML div ref 'map'. Returned in render below.
      const node = ReactDOM.findDOMNode(mapRef); // finds the 'map' div in the React DOM, names it node

      const forShowingCluesInGameMap = !!subBoundsCallback;

      // zoom from bounds;    Lower numbers are zoomed further out.
      let zoom = zoomFromBounds(gBounds, this.mapDim());

      if (forShowingCluesInGameMap) {
         zoom -= 0.1;
      } else {
         zoom -= 1.8;
         zoom = Math.max(zoom, 2);
      }

      // zoom out one unless we're using bounds for scale
      // if (!isClue && !hasSubBounds)
      //    zoom--;

      const mapConfig = Object.assign({}, {
         center: gBounds.getCenter(),
         zoom: zoom,
         fullscreenControl: false,
         draggable: false,
         disableDefaultUI: true
      });

      this.map = new maps.Map(node, mapConfig); // creates a new Google map on the specified node (ref='map') with the specified configuration set above.

      // Add the bounds rect if bounds exist.
      if (hasBounds) {
         this.addBoundsRectToMap(gBounds, this.map, isClue, hasSubBounds);
      }

      if (hasSubBounds) {
         this.addSubBoundsToMap(subBounds, this.map, subBoundsCallback);
      }

      if (this.props.editable) {
         this.map.addListener('click', this.props.editCallback);
      }
   }

    static defaultBoundingBoxForLocation(latDef, lonDef, google) {
        // make it about the size of a few US states.
        const west = lonDef - 1.0;
        const east = lonDef + 1.0;
        const north = latDef + 0.6;
        const south = latDef - 0.6;

        return new google.maps.LatLngBounds(
            new google.maps.LatLng(south, west),
            new google.maps.LatLng(north, east)
        );
    }

    // turns lat-lon bounds into the Rectangle shape on the map.
    addBoundsRectToMap(rect, map, isClue, hasSubBounds) {

        const { google } = this.props;

        if (!google) {
            return;
        }

        const color = isClue ? '#00A0C0'
            : '#007000';

        const opacity = hasSubBounds ? 0.0 : 0.15;

        const boundsRect = new google.maps.Rectangle({
            bounds: rect,
            strokeColor: color,
            strokeOpacity: 0.8,
            strokeWeight: 2.5,
            fillColor: color,
            fillOpacity: opacity,
            clickable: false
        });

        boundsRect.setMap(map);
        this.setState({ googleRect : boundsRect});
    }

    addSubBoundsToMap(rects, map, rectCallback) {

        const { google } = this.props;
        if (!google) {
            return;
        }

        const color = '#00A0C0';    // assumes sub bounds are for clues
        const opacity = 0.1;
        const clickable = !!rectCallback;

        for (let i=0; i < rects.length; i++) {

            const rect = rects[i];
            if (!rect) {
                continue;
            }

            // google api doesn't like our rect object here, need to create proper map latlngbounds.
            const sw = new google.maps.LatLng(rect.southwest.latitude, rect.southwest.longitude);
            const ne = new google.maps.LatLng(rect.northeast.latitude, rect.northeast.longitude);
            const latLngBounds = new google.maps.LatLngBounds(sw, ne);

            const boundsRect = new google.maps.Rectangle({
                bounds: latLngBounds,
                strokeColor: color,
                strokeOpacity: 0.8,
                strokeWeight: 1.8,
                fillColor: color,
                fillOpacity: opacity,
                clickable: clickable
            });
            if (clickable) {
                const clueNumber = i+1;
                boundsRect.addListener('click', (_) => { rectCallback(clueNumber) });
            }
            boundsRect.setMap(map);
        }
    }

    // Returns true if the bounds specified in props changed.
    shouldComponentUpdate(nextProps, nextState) {

        if (this.props.bounds) {

            if (nextProps.bounds) {

                return !(this.props.bounds.southwest === nextProps.bounds.southwest
                    && this.props.bounds.northeast === nextProps.bounds.northeast);
            } else {
                return true;
            }

        } else {
            // if previous bounds does not exist, return false if new one also does not exist, true if it does.
            return !!nextProps.bounds;
        }
    }

    render() {

        const { bounds, caption, editable, editCallback } = this.props;
        const isModal = !!this.props.closeModalCallback;

        const mapDim = this.mapDim();
        const style = {
            height: `${mapDim.height}px`,
            width: `${mapDim.width}px`
        };
        const noTopPadding = {
            paddingTop: '0px'
        };

        const captionOnMap = (bounds ? caption : "No bounds. " );
        const figCaptionStyle = bounds ? { } : { color: 'red' };
        const editText = editable
                ? bounds ? "(Edit)" : "(Add bounds)"
                : "";

        // this.ref.maps
        return (
            <div>
                {isModal ?
                    <button
                        style={noTopPadding}
                        className="btn btn-link pull-right bottom-margin"
                        onClick={this.props.closeModalCallback}>

                        X
                    </button>
                    :  ""
                }

                <figure>
                <div
                    style={style}
                    className="map-image"
                    ref={"map"} />

                <figcaption
                    className="left-margin"
                    style={figCaptionStyle}>

                    {captionOnMap}
                    {editable ?
                        <button
                            className="btn btn-link accent-link"
                            onClick={editCallback}>
                            {editText}
                        </button>
                        : ""
                    }
                </figcaption>
            </figure>
            </div>
            );
    }

   mapDim() {
      const h = (this.props.height ? this.props.height : 200);
      const w = (this.props.width ? this.props.width : 240);
      return {width: w, height: h}
   }
}

// OTHER MOST IMPORTANT: Here we are exporting the App component WITH the GoogleApiWrapper. You pass it down with an object containing your API key
export default GoogleApiWrapper({
    apiKey: 'AIzaSyDAAYp_Ao7KMwxYxJFiIbutXuz4yyjeey8',
})(BoundsOnMap);


// WEBPACK FOOTER //
// ./src/components/huntedit/BoundsOnMap.js